import { api, apiClient } from "../lib";
import type { DateOnlyString } from "../utils/dateOnly";
import { normalizeDateOnlyQueryRange } from "./dateOnlyQuery";
import {
  getResponseDownloadFilename,
  triggerBlobDownload,
} from "../utils/downloadFilename";

export interface CorrectionRequest {
  id: string;
  status: "pending" | "applied" | "modified_and_applied" | "discarded";
  source?: "correction_request" | "direct_correction";
  sourceRecordId?: string;
  auditRecordId?: string;
  requestedAt: string;
  requestor: { id: string; name: string; email: string };
  requestorNote: string | null;
  proposedChanges: Record<string, { before: any; after: any }>;
  resolvedAt?: string;
  resolvedBy?: { id: string; name: string; email: string };
  resolutionNote?: string | null;
  appliedChanges?: Record<string, { before: any; after: any }>;
  caseErrorReportId?: string | null;
  caseErrorReportRefNumber?: number | null;
  refNumber?: number;
}

export interface CorrectionQueueItem {
  id: string;
  caseId: string;
  caseNumber: string | null;
  patientName: string | null;
  correctedById: string;
  correctedByName: string | null;
  accessionedByName: string | null;
  correctedAt: string;
  dateReceived: DateOnlyString | null;
  dateCollected: DateOnlyString | null;
  dateReported: DateOnlyString | null;
  status: "corrected";
  proposedFieldCount: number;
  hasNote: boolean;
  correctionNote: string | null;
  proposedChanges: Record<string, { before: any; after: any }> | null;
  caseErrorReportId: string | null;
  caseErrorReportRefNumber?: number | null;
  refNumber?: number | null;
}

export interface CorrectionClinicalDateFilters {
  dateReceivedFrom?: DateOnlyString;
  dateReceivedTo?: DateOnlyString;
  dateCollectedFrom?: DateOnlyString;
  dateCollectedTo?: DateOnlyString;
  dateReportedFrom?: DateOnlyString;
  dateReportedTo?: DateOnlyString;
}

function appendClinicalDateFilters(
  query: URLSearchParams,
  filters: CorrectionClinicalDateFilters
) {
  (
    [
      ["dateReceived", filters.dateReceivedFrom, filters.dateReceivedTo],
      ["dateCollected", filters.dateCollectedFrom, filters.dateCollectedTo],
      ["dateReported", filters.dateReportedFrom, filters.dateReportedTo],
    ] as const
  ).forEach(([name, from, to]) => {
    const range = normalizeDateOnlyQueryRange(
      { from, to },
      {
        from: `${name}From`,
        to: `${name}To`,
      }
    );
    if (range.from) query.set(`${name}From`, range.from);
    if (range.to) query.set(`${name}To`, range.to);
  });
}

export interface AllCorrectionRequestsResponse {
  success: boolean;
  correctionRequests: CorrectionQueueItem[];
  total: number;
  limit: number;
  offset: number;
}

export interface CorrectionsReportData {
  generatedAt: string;
  rows: Array<{
    caseNumber: string | null;
    changes: string;
    accessionedBy: string | null;
    correctedBy: string | null;
    correctedAt: string;
  }>;
}

export interface CorrectionRequestsResponse {
  success: boolean;
  caseId: string;
  correctionRequests: CorrectionRequest[];
  pagination: { total: number; limit: number; offset: number };
}

export interface WorkQueueItem {
  type: "sign-off-pending" | "correction-request-pending";
  caseId: string;
  caseNumber: string;
  patientName: string;
  daysInQueue: number;
  navigateUrl: string;
  // sign-off-pending specific
  procedure?: string;
  grossedAt?: string;
  // correction-request-pending specific
  correctionRequestId?: string;
  requestorName?: string;
  requestedAt?: string;
  proposedFieldCount?: number;
  hasNote?: boolean;
}

export interface WorkQueueResponse {
  success: boolean;
  pathologistId: string;
  workQueueItems: WorkQueueItem[];
  pagination: { total: number; limit: number; offset: number };
  prefixOptions?: string[];
}

export interface CorrectionWorkQueueFilters {
  search?: string;
  prefixKeys?: string[];
  sorts?: Array<{ column: string; direction: "asc" | "desc" }>;
}

export interface CorrectionWorkQueuePage {
  items: WorkQueueItem[];
  total: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
  prefixOptions: string[];
}

export interface ApplyResponse {
  success: boolean;
  message: string;
  correctionRequestId: string;
  caseId: string;
  status: string;
  appliedChanges?: Record<string, { before: any; after: any }>;
  amendmentId?: string;
  reportVersionIncremented?: boolean;
}

export const correctionRequestService = {
  getCorrectionRequests: async (
    caseId: string,
    statusFilter?: string,
    limit = 50,
    offset = 0,
    includeHistory = false
  ): Promise<CorrectionRequestsResponse> => {
    const params = new URLSearchParams({
      limit: String(limit),
      offset: String(offset),
    });
    if (statusFilter) params.set("status", statusFilter);
    if (includeHistory) params.set("includeHistory", "true");
    return api.get(`/cases/${caseId}/correction-requests?${params}`);
  },

  getWorkQueue: async (limit = 50, offset = 0): Promise<WorkQueueResponse> => {
    return api.get(`/pathologist/work-queue?limit=${limit}&offset=${offset}`);
  },

  getPagedWorkQueue: async (
    filters: CorrectionWorkQueueFilters,
    page: number,
    pageSize = 20
  ): Promise<CorrectionWorkQueuePage> => {
    const params = new URLSearchParams({
      limit: String(pageSize),
      offset: String((page - 1) * pageSize),
    });
    if (filters.search?.trim()) params.set("search", filters.search.trim());
    filters.prefixKeys?.forEach((prefixKey) =>
      params.append("prefixKeys", prefixKey)
    );
    if (filters.sorts?.length)
      params.set("sorts", JSON.stringify(filters.sorts));
    const response = await api.get<WorkQueueResponse>(
      `/pathologist/work-queue?${params}`
    );
    const { total, limit, offset } = response.pagination;
    return {
      items: response.workQueueItems,
      total,
      page,
      pageSize: limit,
      hasMore: offset + response.workQueueItems.length < total,
      prefixOptions: response.prefixOptions ?? [],
    };
  },

  applyCorrectionRequest: async (
    requestId: string,
    note?: string
  ): Promise<ApplyResponse> => {
    return api.post(`/correction-requests/${requestId}/apply`, { note });
  },

  applyModifiedCorrectionRequest: async (
    requestId: string,
    appliedChanges: Record<string, any>,
    note?: string
  ): Promise<ApplyResponse> => {
    return api.post(`/correction-requests/${requestId}/apply-modified`, {
      ...appliedChanges,
      note,
    });
  },

  discardCorrectionRequest: async (
    requestId: string,
    note?: string
  ): Promise<ApplyResponse> => {
    return api.post(`/correction-requests/${requestId}/discard`, { note });
  },

  listAllCorrectionRequests: async (
    params: CorrectionClinicalDateFilters & {
      limit?: number;
      offset?: number;
      dateFrom?: DateOnlyString;
      dateTo?: DateOnlyString;
      search?: string;
    }
  ): Promise<AllCorrectionRequestsResponse> => {
    const dateRange = normalizeDateOnlyQueryRange(
      { from: params.dateFrom, to: params.dateTo },
      { from: "dateFrom", to: "dateTo" }
    );
    const query = new URLSearchParams({
      limit: String(params.limit ?? 20),
      offset: String(params.offset ?? 0),
    });
    if (dateRange.from) query.set("dateFrom", dateRange.from);
    if (dateRange.to) query.set("dateTo", dateRange.to);
    appendClinicalDateFilters(query, params);
    if (params.search?.trim()) query.set("search", params.search.trim());
    return api.get(`/correction-requests?${query}`);
  },

  exportCorrections: async (
    format: "excel" | "pdf",
    params: CorrectionClinicalDateFilters & {
      dateFrom?: DateOnlyString;
      dateTo?: DateOnlyString;
      search?: string;
    }
  ): Promise<void> => {
    const dateRange = normalizeDateOnlyQueryRange(
      { from: params.dateFrom, to: params.dateTo },
      { from: "dateFrom", to: "dateTo" }
    );
    const query = new URLSearchParams();
    if (dateRange.from) query.set("dateFrom", dateRange.from);
    if (dateRange.to) query.set("dateTo", dateRange.to);
    appendClinicalDateFilters(query, params);
    if (params.search?.trim()) query.set("search", params.search.trim());
    const response = await apiClient.get(
      `/correction-requests/export/${format}?${query}`,
      {
        responseType: "blob",
        timeout: 120_000,
      }
    );
    const fallback = `corrections.${format === "excel" ? "xlsx" : "pdf"}`;
    triggerBlobDownload(
      response.data as Blob,
      getResponseDownloadFilename(
        response.headers["content-disposition"],
        fallback
      )
    );
  },

  updateCorrectionRequest: async (
    requestId: string,
    body: Record<string, any>
  ): Promise<{
    success: boolean;
    message: string;
    correctionRequestId: string;
    caseId: string;
  }> => {
    return api.put(`/correction-requests/${requestId}`, body);
  },

  linkToErrorReport: async (
    requestId: string,
    caseErrorReportId: string | null
  ): Promise<{ success: boolean }> => {
    return api.patch(`/correction-requests/${requestId}/link-error`, {
      caseErrorReportId,
    });
  },
};
